import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiExternalLink, FiUser, FiGlobe, FiImage, FiUpload, FiTrash2 } from 'react-icons/fi';
import { toast } from '../../Toast';
import Toggle from '../../Toggle';
import { BackLink, HubHeader, SelectBox, hubPath, useHubSite } from '../shared';
import { CATEGORIES, Category, PEOPLE, groupOf } from './BoardManagement';

const ordinal = (n: number) => {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return `${n}th`;
    const last = n % 10;
    if (last === 1) return `${n}st`;
    if (last === 2) return `${n}nd`;
    if (last === 3) return `${n}rd`;
    return `${n}th`;
};

// Website Hub › Board & management › Add a person / edit (?id=) (spec 409:11430)
const AddPerson: React.FC = () => {
    const history = useHistory();
    const { search } = useLocation();
    const { host, short, url } = useHubSite();
    const editing = PEOPLE.find(p => p.id === new URLSearchParams(search).get('id'));
    const fileInput = useRef<HTMLInputElement>(null);

    const [name, setName] = useState(editing?.name ?? '');
    const [role, setRole] = useState(editing?.role ?? '');
    const [category, setCategory] = useState<Category>(editing?.category ?? 'Directors');
    const [qualifications, setQualifications] = useState(editing?.qualifications ?? '');
    const [bio, setBio] = useState((editing?.bio ?? '').replace(/49 Metals/g, short));
    const [linkedin, setLinkedin] = useState(editing?.linkedin ?? '');
    const [email, setEmail] = useState(editing?.email ?? '');
    const [visible, setVisible] = useState(editing?.visible ?? true);
    const [photo, setPhoto] = useState<string | null>(null);

    // "Position in the list": slots among the others in the same group, in website order
    const positionsFor = (c: Category) => ['1st — top of the list',
        ...PEOPLE.filter(p => groupOf(p.category) === groupOf(c) && p.id !== editing?.id).map((p, i) => `${ordinal(i + 2)} — after ${p.name}`)];
    const positions = positionsFor(category);
    const [position, setPosition] = useState(() => {
        const own = editing ? PEOPLE.filter(p => groupOf(p.category) === groupOf(editing.category)).findIndex(p => p.id === editing.id) : -1;
        return positions[own >= 0 ? own : positions.length - 1];
    });
    const changeCategory = (c: Category) => {
        setCategory(c);
        // another group has another list; start at the end of it
        if (groupOf(c) !== groupOf(category)) setPosition(positionsFor(c).slice(-1)[0]);
    };

    const pickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setPhoto(URL.createObjectURL(file));
    };

    const save = () => {
        if (!name.trim()) {
            toast.error('Add the person’s full name first');
            return;
        }
        toast.info(`Preview only: ${name.trim()} would be saved ${visible ? 'and shown on the website' : 'as hidden'} — nothing was sent to WordPress`);
        history.push(hubPath('content', 'board-management'));
    };

    return <>
        <BackLink to={hubPath('content', 'board-management')} label="Back to Board & management" />
        <HubHeader title={editing ? 'Edit a Person' : 'Add a Person'} sub={`They will appear on ${host}/about-us/board-management/`}>
            <a className="btn-outline with-icon" href={url('/about-us/board-management/')} target="_blank" rel="noopener noreferrer"
                onClick={e => { e.preventDefault(); toast.info('Preview only: the WordPress preview isn’t connected yet'); }}><FiExternalLink /> Preview on the Website</a>
            <button type="button" className="btn-primary" onClick={save}>{visible ? 'Save & Show on Website' : 'Save as Hidden'}</button>
        </HubHeader>

        <div className="cs-row hub-person-form">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUser /></span>
                        <div className="text"><h2>Details</h2></div>
                    </div>

                    <div className="field">
                        <span className="label">Photo</span>
                        <div className="hub-photo-row">
                            <span className="hub-photo-drop">
                                {photo ? <img src={photo} alt="" /> : <><FiImage /><span>Square</span></>}
                            </span>
                            <div>
                                <p className="hint">Square JPG or PNG, at least 1000 × 1000 px — the website shows these large, so a small photo will look soft.</p>
                                <span className="hub-photo-buttons">
                                    <button type="button" className="btn-outline with-icon" onClick={() => fileInput.current?.click()}><FiUpload /> Upload a Photo</button>
                                    <button type="button" className="btn-outline with-icon" disabled={!photo} onClick={() => setPhoto(null)}><FiTrash2 /> Remove</button>
                                </span>
                                <input ref={fileInput} type="file" accept="image/jpeg,image/png" hidden onChange={pickPhoto} />
                            </div>
                        </div>
                    </div>

                    <div className="fields">
                        <label className="field">
                            <span className="label">Full name</span>
                            <input value={name} placeholder="Dr Sarah Nguyen" onChange={e => setName(e.target.value)} />
                        </label>
                        <label className="field">
                            <span className="label">Title or role</span>
                            <input value={role} placeholder="Non-Executive Director" onChange={e => setRole(e.target.value)} />
                        </label>
                        <div className="field">
                            <span className="label">Category</span>
                            <SelectBox className="hub-select-full" label="Category" value={category} options={CATEGORIES} onChange={v => changeCategory(v as Category)} />
                            <span className="hub-field-hint">Directors · Management · Company Secretary — decides the group on the website and which embed code includes them.</span>
                        </div>
                        <label className="field">
                            <span className="label">Qualifications<em>(optional)</em></span>
                            <input value={qualifications} placeholder="BSc (Geology), PhD, MAusIMM" onChange={e => setQualifications(e.target.value)} />
                        </label>
                    </div>

                    <label className="field">
                        <span className="label">Biography<em>· plain language, 2–4 short paragraphs</em></span>
                        <textarea className="hub-bio" value={bio} placeholder="Who they are, their experience and what they do for the company." onChange={e => setBio(e.target.value)} />
                    </label>

                    <div className="fields">
                        <label className="field">
                            <span className="label">LinkedIn profile<em>(optional)</em></span>
                            <input value={linkedin} placeholder="linkedin.com/in/…" onChange={e => setLinkedin(e.target.value)} />
                        </label>
                        <label className="field">
                            <span className="label">Email shown publicly<em>(optional)</em></span>
                            <input type="email" value={email} placeholder="Leave blank to hide" onChange={e => setEmail(e.target.value)} />
                        </label>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiGlobe /></span>
                        <div className="text"><h2>On the website</h2></div>
                    </div>
                    <div className="setting-row">
                        <div className="text"><span className="title">Show on the website</span><span className="hint">Turn off to hide without deleting</span></div>
                        <Toggle checked={visible} onChange={setVisible} label="Show on the website" />
                    </div>
                    <div className="field">
                        <span className="label">Position in the list</span>
                        <SelectBox className="hub-select-full" label="Position in the list" value={position} options={positions} onChange={setPosition} />
                    </div>
                </section>
            </div>
        </div>

        <p className="hub-foot-note"><FiGlobe /> Publishing creates this person’s page on the website, with the biography above as the body. Editing it here updates that page — it is never mixed into news and media.</p>
    </>;
};

export default AddPerson;
